import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Building2, Settings, FileText, ArrowRight, Shield, Mail } from 'lucide-react';

export default function MerciAnnuelPage() {
  const { state } = useLocation();
  const prenom = state?.prenom || '';
  const email = state?.email || '';
  const accessLinkSent = !!state?.accessLinkSent;
  const requiresLogin = !!state?.requiresLogin;
  const isMonthly = state?.plan === 'monthly';

  const steps = [
    {
      icon: Building2,
      num: '1',
      title: 'Complétez les informations de votre entreprise',
      desc: 'Nom, SIRET, adresse : indispensables pour des factures conformes.',
    },
    {
      icon: Settings,
      num: '2',
      title: 'Paramétrez votre situation',
      desc: 'Activité (BIC/BNC), TVA, versement libératoire le cas échéant — vos calculs seront justes dès le départ.',
    },
    {
      icon: FileText,
      num: '3',
      title: 'Découvrez votre tableau de bord et créez votre première facture',
      desc: 'CA, cotisations, échéances : tout est calculé automatiquement.',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="merci-annuel-page">
      <header className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-3xl mx-auto px-4">
          <span className="font-bold text-lg font-['Manrope']">
            <span className="text-emerald-600">Capsule</span> Patrimoine
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        {/* Confirmation */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold font-['Manrope'] text-slate-900 mb-3">
            {prenom ? `Merci ${prenom} !` : 'Merci !'} Votre paiement est enregistré
          </h1>
          <p className="text-slate-600 max-w-xl mx-auto" data-testid="merci-annuel-confirmation">
            Votre <strong>{isMonthly ? 'abonnement mensuel MON AE (29,99€/mois)' : 'abonnement annuel MON AE (299€/an)'}</strong> est activé.
            {email && <> Un email de confirmation a été envoyé à <strong>{email}</strong>.</>}
          </p>
        </div>

        {/* Compte existant : l'abonnement est activé, mais aucune session automatique (sécurité) */}
        {requiresLogin && (
          <Card className="border-emerald-200 bg-emerald-50 mb-8" data-testid="merci-annuel-existing-account">
            <CardContent className="p-5">
              <p className="text-sm font-semibold text-emerald-800 mb-2">
                🔐 Votre abonnement est activé sur votre compte existant.
              </p>
              <p className="text-sm text-emerald-900" data-testid="existing-account-info">
                Par sécurité, aucune connexion automatique n'est effectuée : connectez-vous avec vos identifiants habituels{email && <> (<strong>{email}</strong>)</>}. Un email de confirmation avec un lien sécurisé vous a également été envoyé.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Accès sécurisé (nouveau compte) : lien à usage unique envoyé par email, aucun mot de passe affiché ni transmis */}
        {accessLinkSent && !requiresLogin && (
          <Card className="border-emerald-200 bg-emerald-50 mb-8" data-testid="merci-annuel-credentials">
            <CardContent className="p-5">
              <p className="text-sm font-semibold text-emerald-800 mb-2">
                🔐 Vous êtes connecté. Pour vos prochaines connexions, définissez votre mot de passe :
              </p>
              <p className="text-sm text-emerald-900" data-testid="access-link-info">
                Un email contenant un <strong>lien sécurisé à usage unique</strong> (valable 48 h) vient de vous être envoyé{email && <> à <strong>{email}</strong></>}. Aucun mot de passe ne transite par email.
              </p>
              <p className="text-xs text-emerald-700 mt-2">
                Email non reçu ? Vérifiez vos indésirables, ou utilisez « Mot de passe oublié » sur la page de connexion pour recevoir un nouveau lien.
              </p>
            </CardContent>
          </Card>
        )}

        {/* 3 premières étapes */}
        <h2 className="text-lg font-bold font-['Manrope'] text-slate-900 mb-4 text-center">
          Vos 3 premières étapes
        </h2>
        <div className="space-y-4 mb-10">
          {steps.map((s) => (
            <Card key={s.num} className="border-slate-200" data-testid={`merci-annuel-step-${s.num}`}>
              <CardContent className="p-5 flex items-start gap-4">
                <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <s.icon className="h-5 w-5 text-emerald-700" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">
                    <span className="text-emerald-700 mr-1">{s.num}.</span>{s.title}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to={requiresLogin ? '/login' : '/dashboard'} data-testid="merci-annuel-cta">
            <Button size="lg" className="h-14 px-10 text-base font-bold bg-emerald-600 hover:bg-emerald-700">
              {requiresLogin ? 'Me connecter à MON AE' : 'Accéder à MON AE'} <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
          <p className="text-xs text-slate-500 mt-3">
            {requiresLogin ? 'Connectez-vous pour retrouver votre espace — votre abonnement est déjà actif.' : 'Vous êtes déjà connecté(e) — votre espace est prêt.'}
          </p>
        </div>

        {/* Rassurance */}
        <div className="flex items-center justify-center gap-6 text-xs text-slate-500 mt-10 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Shield className="h-3.5 w-3.5 text-emerald-600" /> Garantie 14 jours satisfait ou remboursé
          </span>
          <span className="inline-flex items-center gap-1">
            <Mail className="h-3.5 w-3.5 text-emerald-600" /> Un problème ? contact@capsulepatrimoine.com
          </span>
        </div>
      </main>
    </div>
  );
}
